import {useMutation, useQueryClient} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {ProblemDetails} from "@/@types/base.types.ts";
import {ErrorKey, getErrorKey} from "@/config/apiErrorKeys.ts";
import {useAxiosAuth} from "@/hooks/useAxiosAuth.ts";
import {ApiConfig} from "@/config/api.config.ts";
import EToast from '@/components/everridge/EToast';
import { t } from 'i18next';

export type OwnerPermissionsActionType =
    "createBookings"
    | "placeOrders"
    | "showCalendar"
    | "showFinances"
    | "loginToAccount"
    | "bookingDurationLimits" 
    | "yearlyBookingLimit";

type DoSetPermissionProps = {
    ownerId: string,
    value: boolean | number
}

type Props = {};

export const useOwnerPermissionsMutateApi = ({}: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();

    const {mutateAsync: doSetShowCalendar, isPending: isDoSetShowCalendarLoading} = useMutation({
        mutationFn: async ({ownerId, value}: DoSetPermissionProps) => {
            const url = ApiConfig.queries.features.owners.permissions.setShowCalendar.endpoint.replace("{ownerId}", ownerId).replace("{value}", value.toString());
            const response = await axiosInstance.patch(url);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.owners.permissions.get]
            });
            onSuccess?.("showCalendar");
        },
        onError: (error: AxiosError<ProblemDetails>) => {
            onError?.("showCalendar", getErrorKey(error));
        }
    });

    const {mutateAsync: doSetShowFinances, isPending: isDoSetShowFinancesLoading} = useMutation({
        mutationFn: async ({ownerId, value}: DoSetPermissionProps) => {
            const url = ApiConfig.queries.features.owners.permissions.setShowFinances.endpoint.replace("{ownerId}", ownerId).replace("{value}", value.toString());
            const response = await axiosInstance.patch(url);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.owners.permissions.get]
            });
            onSuccess?.("showFinances");
        },
        onError: (error: AxiosError<ProblemDetails>) => {
            onError?.("showFinances", getErrorKey(error));
        }
    });

    const {mutateAsync: doSetCreateBookings, isPending: isDoSetCreateBookingsLoading} = useMutation({
        mutationFn: async ({ownerId, value}: DoSetPermissionProps) => {
            const url = ApiConfig.queries.features.owners.permissions.setCreateBookings.endpoint.replace("{ownerId}", ownerId).replace("{value}", value.toString());
            const response = await axiosInstance.patch(url);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.owners.permissions.get]
            });
            onSuccess?.("createBookings");
        },
        onError: (error: AxiosError<ProblemDetails>) => {
            onError?.("createBookings", getErrorKey(error));
        }
    });

    const {mutateAsync: doSetPlaceOrders, isPending: isDoSetPlaceOrdersLoading} = useMutation({
        mutationFn: async ({ownerId, value}: DoSetPermissionProps) => {
            const url = ApiConfig.queries.features.owners.permissions.setPlaceOrders.endpoint.replace("{ownerId}", ownerId).replace("{value}", value.toString());
            const response = await axiosInstance.patch(url);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.owners.permissions.get]
            });
            onSuccess?.("placeOrders");
        },
        onError: (error: AxiosError<ProblemDetails>) => {
            onError?.("placeOrders", getErrorKey(error));
        }
    });

    const {mutateAsync: doSetLoginToAccount, isPending: isDoSetLoginToAccountLoading} = useMutation({
        mutationFn: async ({ownerId, value}: DoSetPermissionProps) => {
            const url = ApiConfig.queries.features.owners.permissions.setLoginToAccount.endpoint.replace("{ownerId}", ownerId).replace("{value}", value.toString());
            const response = await axiosInstance.patch(url);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.owners.permissions.get]
            });
            onSuccess?.("loginToAccount");
        },
        onError: (error: AxiosError<ProblemDetails>) => {
            onError?.("loginToAccount", getErrorKey(error));
        }
    });

    const {mutateAsync: doSetBookingDurationLimits, isPending: isDoSetBookingDurationLimitsLoading} = useMutation({
        mutationFn: async ({ownerId, value}: DoSetPermissionProps) => {
            const url = ApiConfig.queries.features.owners.permissions.setBookingDurationLimits.endpoint.replace("{ownerId}", ownerId).replace("{value}", value.toString());
            const response = await axiosInstance.patch(url);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.owners.permissions.get]
            });
            onSuccess?.("bookingDurationLimits");
        },
        onError: (error: AxiosError<ProblemDetails>) => {
            onError?.("bookingDurationLimits", getErrorKey(error));
        }
    });

    const {mutateAsync: doSetYearlyBookingLimit, isPending: isDoSetYearlyBookingLimitLoading} = useMutation({
        mutationFn: async ({ownerId, value}: DoSetPermissionProps) => {
            const url = ApiConfig.queries.features.owners.permissions.setYearlyBookingLimit.endpoint.replace("{ownerId}", ownerId).replace("{value}", value.toString());
            const response = await axiosInstance.patch(url);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.owners.permissions.get]
            });
            onSuccess?.("yearlyBookingLimit");
        },
        onError: (error: AxiosError<ProblemDetails>) => {
            onError?.("yearlyBookingLimit", getErrorKey(error));
        }
    });

    function onSuccess(actionType: OwnerPermissionsActionType) {
		EToast({
			variant: 'success',
			message: `${t('main.owners.toasts.success.messagePart1')} ${actionType} ${t('main.owners.toasts.success.messagePart2')}`,
			description: `${t('main.owners.toasts.success.descriptionPart1')} ${actionType} ${t('main.owners.toasts.success.descriptionPart2')}`,
		});
	};

	function onError(actionType: OwnerPermissionsActionType, error: ErrorKey) {
		EToast({
			variant: 'error',
			message: `${t('main.owners.toasts.error.message')} ${actionType}}`,
			description: `${t('main.owners.toasts.error.descriptionPart1')} ${actionType} ${t('main.owners.toasts.error.descriptionPart2')}`,
		});
	};

    return {
        doSetShowCalendar,
        isDoSetShowCalendarLoading,
        doSetShowFinances,
        isDoSetShowFinancesLoading,
        doSetCreateBookings,
        isDoSetCreateBookingsLoading,
        doSetPlaceOrders,
        isDoSetPlaceOrdersLoading,
        doSetLoginToAccount,
        isDoSetLoginToAccountLoading,
        doSetBookingDurationLimits,
        isDoSetBookingDurationLimitsLoading,
        doSetYearlyBookingLimit,
        isDoSetYearlyBookingLimitLoading
    }
}